/**
 * @constructor
 * @param {string} id
 * @param {string} title
 * @param {boolean} canFullScreen
 * @param {boolean} isResizable
 * @param {boolean} isMovable
 * @param {string} container
 * @param {string} body
 * @param {string} header
 * @param {function} onBeforeResize
 * @param {function} onAfterResize
 * @param {function} onBeforeFullscreen
 * @param {function} onAfterFullscreen
 */

/**
 * add this link/css to html file
 * <link rel="stylesheet" type="text/css" href="/WebInterface/new-ui/modules/resizable/resizable.css" />
 */

import { _$ } from "/WebInterface/new-ui/assets/js/utils/dom.js";
import resizableTemplate from "./template.js";
let id_counter = 0;

export default class Resizable {
  constructor({
    id,
    title,
    isFullScreen = false,
    canFullScreen = true,
    isResizable = true,
    isMovable = true,
    container,
    body,
    header,
    onBeforeResize = () => {},
    onAfterResize = () => {},
    onBeforeFullscreen = () => {},
    onAfterFullscreen = () => {},
  }) {
    this.title = title;
    this.isFullScreen = isFullScreen;
    this.canFullScreen = canFullScreen;
    this.isResizable = isResizable;
    this.isMovable = isMovable;
    this.container = container;
    this.header = header;
    this.body = body;
    this.onBeforeResize = onBeforeResize;
    this.onAfterResize = onAfterResize;
    this.onBeforeFullscreen = onBeforeFullscreen;
    this.onAfterFullscreen = onAfterFullscreen;
    this.id = id || `resizable-${++id_counter}`;
  }

  init = async () => {
    let original_width = 0;
    let original_height = 0;
    let original_x = 0;
    let original_y = 0;
    let original_mouse_x = 0;
    let original_mouse_y = 0;

    if (!this.container) {
      this.container = document.createElement("div");
      this.container.setAttribute("id", this.id);
      this.container.classList.add("resizable-container");
      this.container.classList.add("resizable-wrapper");
      this.container.innerHTML = resizableTemplate({
        title: this.title,
      });
      document.body.appendChild(this.container);
    }

    const container = _$(this.container).el();
    utils.centerModal({ child: container });

    const header = this.header || _$(container).find("header", true);
    const resizeButton = _$(container).find(".resize-button", true);
    const closeButton = _$(container).find(".remove-button", true);
    const resizableBody = _$(container).find(".resizable-body", true);

    if (this.canFullScreen && resizeButton) {
      resizeButton.classList.remove("is-hidden");

      const doFullScreen = () => {
        if (container.classList.contains("full-screen")) {
          const resizeIcon = _$(resizeButton).find(".icon-resize-small", true);
          resizeIcon.classList.remove("icon-resize-small");
          resizeIcon.classList.add("icon-resize-full");
          container.classList.remove("full-screen");
        } else {
          const resizeIcon = _$(resizeButton).find(".icon-resize-full", true);
          resizeIcon.classList.remove("icon-resize-full");
          resizeIcon.classList.add("icon-resize-small");
          container.classList.add("full-screen");
        }
      };

      if (this.isFullScreen) {
        doFullScreen();
      }
      resizeButton.addEventListener("click", (event) => {
        event.preventDefault();

        this.onBeforeFullscreen();
        doFullScreen();
        this.onAfterFullscreen();
      });
    }

    if (closeButton) {
      closeButton.addEventListener("click", (event) => {
        this.hide();
      });
    }

    if (this.body) {
      resizableBody.append(this.body);
    }

    if (this.isMovable) {
      const handle_mousemove = (event) => {
        const x = event.clientX;
        const y = event.clientY;
        const root_w = document.documentElement.clientWidth;
        const root_h = document.documentElement.clientHeight;

        const maxLeft = root_w - container.clientWidth;
        const maxTop = root_h - container.clientHeight;

        const leftPosition =
          x + this.offsetX > maxLeft ? maxLeft : x + this.offsetX;
        const topPosition =
          y + this.offsetY > maxTop ? maxTop : y + this.offsetY;

        if (!this.resize) {
          // Round dynamic values to nearest 10px for utility class compatibility
          const roundedLeft = Math.round((leftPosition > 0 ? leftPosition : 0) / 10) * 10;
          const roundedTop = Math.round((topPosition > 0 ? topPosition : 0) / 10) * 10;
          // Use CSS custom properties instead of inline styles for CSP compliance
          container.style.setProperty("--resizable-left", `${roundedLeft}px`);
          container.style.setProperty("--resizable-top", `${roundedTop}px`);
        }
      };

      const handle_mouseup = () => {
        header.removeEventListener("mousemove", handle_mousemove);

        removeEventListener("mousemove", handle_mousemove);
        removeEventListener("mouseup", handle_mouseup);

        this.removeDummyCover();
      };

      const handle_mousedown = (event) => {
        this.resize = false;

        window.addEventListener("mouseup", handle_mouseup);
        window.addEventListener("mousemove", handle_mousemove);

        header.addEventListener("mouseup", handle_mouseup);
        header.addEventListener("mousemove", handle_mousemove);

        const { left, top } = container.getBoundingClientRect();

        const x = event.clientX;
        const y = event.clientY;

        this.offsetX = left - x;
        this.offsetY = top - y;

        this.addDummyCover();
      };

      if (header) {
        // Cursor style - use CSS class instead
        header.classList.add("cursor-move");
        header.addEventListener("mousedown", handle_mousedown, true);
      }
    }

    if (this.isResizable) {
      const northWestLine =
        _$(container).find(".resizable-nw", true) ||
        container.appendChild(utils.createElement("div", ["resizable-nw"]));

      const northEastLine =
        _$(container).find(".resizable-ne", true) ||
        container.appendChild(utils.createElement("div", ["resizable-ne"]));

      const southEastLine =
        _$(container).find(".resizable-se", true) ||
        container.appendChild(utils.createElement("div", ["resizable-se"]));

      const southWestLine =
        _$(container).find(".resizable-sw", true) ||
        container.appendChild(utils.createElement("div", ["resizable-sw"]));

      const handle_mousemove_resize = (event) => {
        this.onBeforeResize({
          width: original_width,
          height: original_height,
        });

        _$("html").addClass("is-unselectable");

        const pageX = event.pageX;
        const pageY = event.pageY;
        const offsetX = pageX - original_mouse_x;
        const offsetY = pageY - original_mouse_y;

        let resize_w, resize_h, move_x, move_y, width, height, x, y;

        if (
          this.resizeLine === southEastLine ||
          this.resizeLine === northEastLine
        ) {
          width = original_width + offsetX;
          resize_w = 1;
        } else if (
          this.resizeLine === southWestLine ||
          this.resizeLine === northWestLine
        ) {
          x = original_x + offsetX;
          width = original_width - offsetX;
          resize_w = 1;
          move_x = 1;
        }

        if (
          this.resizeLine === southEastLine ||
          this.resizeLine === southWestLine
        ) {
          height = original_height + offsetY;
          resize_h = 1;
        } else if (
          this.resizeLine === northEastLine ||
          this.resizeLine === northWestLine
        ) {
          height = original_height - offsetY;
          y = original_y + offsetY;
          resize_h = 1;
          move_y = 1;
        }

        if (resize_w) {
          const root_w = document.documentElement.clientWidth;
          width = Math.min(width, root_w - original_x);
          // Round dynamic values to nearest 10px for utility class compatibility
          const roundedWidth = Math.round(width / 10) * 10;
          // Use CSS custom properties instead of inline styles for CSP compliance
          container.style.setProperty("--resizable-width", `${roundedWidth}px`);
        }

        if (resize_h) {
          const root_h = document.documentElement.clientHeight;
          height = Math.min(height, root_h - original_y);
          // Round dynamic values to nearest 10px for utility class compatibility
          const roundedHeight = Math.round(height / 10) * 10;
          // Use CSS custom properties instead of inline styles for CSP compliance
          container.style.setProperty("--resizable-height", `${roundedHeight}px`);
        }

        if (move_x) {
          const root_w = document.documentElement.clientWidth;
          x = Math.max(Math.min(x, root_w - original_width), 0);
          // Round dynamic values to nearest 10px for utility class compatibility
          const roundedX = Math.round(x / 10) * 10;
          // Use CSS custom properties instead of inline styles for CSP compliance
          container.style.setProperty("--resizable-left", `${roundedX}px`);
        }

        if (move_y) {
          const root_h = document.documentElement.clientHeight;
          y = Math.max(Math.min(y, root_h - original_height), 0);
          // Round dynamic values to nearest 10px for utility class compatibility
          const roundedY = Math.round(y / 10) * 10;
          // Use CSS custom properties instead of inline styles for CSP compliance
          container.style.setProperty("--resizable-top", `${roundedY}px`);
        }

        this.onAfterResize();
      };

      const handle_mouseup_resize = () => {
        _$("html").removeClass("is-unselectable");
        removeEventListener("mousemove", handle_mousemove_resize);

        this.resizeLine.removeEventListener(
          "mousemove",
          handle_mousemove_resize
        );
        this.resizeLine.removeEventListener("mouseup", handle_mouseup_resize);

        this.removeDummyCover();
      };

      const handle_mousedown_resize = (event) => {
        event.stopPropagation();

        this.resize = true;
        this.resizeLine = event.target;
        original_mouse_x = event.pageX;
        original_mouse_y = event.pageY;
        original_x = container.getBoundingClientRect().left;
        original_y = container.getBoundingClientRect().top;
        original_width = parseFloat(container.clientWidth);
        original_height = parseFloat(container.clientHeight);

        this.resizeLine.addEventListener("mousemove", handle_mousemove_resize);
        this.resizeLine.addEventListener("mouseup", handle_mouseup_resize);

        window.addEventListener("mousemove", handle_mousemove_resize);
        window.addEventListener("mouseup", handle_mouseup_resize);

        this.addDummyCover();
      };

      southWestLine.addEventListener("mousedown", handle_mousedown_resize);
      southEastLine.addEventListener("mousedown", handle_mousedown_resize);
      northEastLine.addEventListener("mousedown", handle_mousedown_resize);
      northWestLine.addEventListener("mousedown", handle_mousedown_resize);
    }
  }

  addDummyCover = () => {
    if (this.container) {
      const body = _$(this.container).find(".modal-card-body", true);
      const iframe = _$(body).find("iframe", true);
      if (iframe) {
        const dummyIframeCover = _$("<div>");
        _$(body).addClass("is-relative");
        dummyIframeCover.addClass("dummy-iframe-wrapper");
        body.append(dummyIframeCover.el());
      }
    }
  }

  removeDummyCover = () => {
    const body = _$(this.container).find(".modal-card-body", true);
    const dummyIframeCover = _$(body).find(".dummy-iframe-wrapper");
    dummyIframeCover.remove();
  }

  show = () => {
    this.init();
  }

  hide = () => {
    _$(`#${this.id}`).remove();
  }
}
